import React, {useEffect, useMemo, useRef, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {
  Box,
  Flex,
  Heading,
  Image,
  Text,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import useIntersectionObserver from '@salesforce/retail-react-app/app/hooks/use-intersection-observer';
import {productUrlBuilder} from '@salesforce/retail-react-app/app/utils/url';

import {RichTextContent} from '@amblique/contentful/app/components/rich-text-content';

/** Same DOM as instock tile — `WANT_IT_SOONER_NAVIGATION` targets `.swiper-navigation-pagination`. */
import {Swiper} from '@amblique/king-living/app/components/shared/swiper';
import InstockProductTile from '@amblique/king-living/app/components/instock-product-tile';
import {
  resolveGa4ListMeta,
  useGa4TileSelect,
  VIEW_ITEM_LIST,
  useGa4ViewItemList,
} from '@amblique/king-living/app/analytics/ga4';
import {useProductDetailContentful} from '@amblique/king-living/app/context/product-detail-contentful-context';
import {scrollToWithHeaderOffset} from '@amblique/king-living/app/utils/utils';

/** Windows-only: wider nudge than theme default (classic scrollbar ~15–17px). */
const WANT_IT_SOONER_WINDOWS_VW_NUDGE = '15px';

/**
 * Detect Windows in the browser (UA, `navigator.platform`, User-Agent Client Hints).
 * Used client-only after mount to avoid SSR/hydration mismatch.
 *
 * @returns {boolean}
 */
function isWindowsClient() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const ua = navigator.userAgent || '';

  if (/Windows/i.test(ua)) {
    return true;
  }

  const platform = navigator.platform || '';

  if (/^Win/i.test(platform)) {
    return true;
  }

  if (navigator.userAgentData?.platform === 'Windows') {
    return true;
  }

  return false;
}

/**
 * Product PDP URL with fallback.
 *
 * Thin wrapper over the centralised `productUrlBuilder` override
 * (`pwa/packages/king-living/app/utils/url.js`) so existing call sites across Want It Sooner,
 * Shop The Look, "See full details" link, Finished Good, Contentful product tiles, etc. keep
 * their import path while picking up the unified resolution rules:
 *   1. `product.c_productPageURL` (Contentful slug) when configured
 *   2. `product.representedProduct.c_productPageURL` (search-hit shape)
 *   3. fallback `/product/{id}` via the upstream builder
 *
 * Previously this returned `null` when no slug was set, which caused tiles in carousels backed
 * by `getProducts` (Want It Sooner) to render without a navigable link whenever a product
 * hadn't been published in Contentful — visually present but not clickable. Returning a
 * fallback path restores navigation; the dynamic-page resolver routes `/product/{id}` to the
 * correct PDP via the default Contentful slug naming convention.
 */
export const getProductUrl = (product) => {
  if (!product) return null;
  return productUrlBuilder(product) || null;
};

/**
 * Promo card for "Want it Sooner" — rendered inside the product-info column.
 * Contentful image + rich text + "View all" scroll link.
 *
 * @param {object} props
 * @param {React.RefObject} props.carouselRef - ref to the carousel section for scroll-to
 * @param {Array} props.instockProducts - required to gate visibility
 */
export function WantItSoonerPromo({carouselRef, instockProducts}) {
  const contentfulData = useProductDetailContentful();
  const styles = useMultiStyleConfig('WantItSooner', {});

  const wantItSoonerContent = contentfulData?.wantItSoonerContent;
  const wantItSoonerImageUrl = contentfulData?.wantItSoonerImage?.url;
  const wantItSoonerImageTitle = contentfulData?.wantItSoonerImage?.title || '';

  if (!wantItSoonerContent && !wantItSoonerImageUrl) return null;
  if (!instockProducts?.length) return null;

  const handleViewAll = () => {
    scrollToWithHeaderOffset(carouselRef.current, undefined, {
      recheckAfterLayout: true,
    });
  };

  return (
    <Box className="want-it-sooner-promo" {...styles.root}>
      <Flex {...styles.promoCard}>
        <Box {...styles.promoContent}>
          {wantItSoonerContent && (
            <RichTextContent content={wantItSoonerContent} />
          )}
          <Text
            as="button"
            onClick={handleViewAll}
            {...styles.viewAllLink}
          >
            <FormattedMessage
              defaultMessage="View all"
              id="want_it_sooner.button.view_all"
            />
          </Text>
        </Box>

        {wantItSoonerImageUrl && (
          <Box {...styles.promoImageWrapper}>
            <Image
              alt={wantItSoonerImageTitle}
              src={wantItSoonerImageUrl}
              {...styles.promoImage}
            />
          </Box>
        )}
      </Flex>
    </Box>
  );
}

WantItSoonerPromo.displayName = 'WantItSoonerPromo';
WantItSoonerPromo.propTypes = {
  carouselRef: PropTypes.object,
  instockProducts: PropTypes.array,
};

/**
 * In-stock product carousel for "Want it Sooner" — full-viewport-bleed white band with inner content capped at 1440px (same padding as before).
 *
 * @param {object} props
 * @param {React.RefObject} props.carouselRef - ref attached to the container for scroll-to
 * @param {Array} props.instockProducts - product objects to display
 */
export function WantItSoonerCarousel({carouselRef, instockProducts}) {
  const intl = useIntl();
  const contentfulData = useProductDetailContentful();
  const styles = useMultiStyleConfig('WantItSooner', {});
  const [windowsVwNudge, setWindowsVwNudge] = useState(null);
  const isOnScreen = useIntersectionObserver(carouselRef, {useOnce: false});

  // KL-1095 — the carousel heading is now authored on the `ProductDetail` Contentful
  // model (`wantItSoonerHeading`, a short text). Fall back to the hardcoded copy when
  // it hasn't been configured so existing PDPs keep rendering the same heading.
  const carouselHeading = contentfulData?.wantItSoonerHeading || intl.formatMessage({
    defaultMessage: 'Your ready stock sofa, without the wait',
    id: 'want_it_sooner.carousel.heading',
  });

  const listMeta = useMemo(
    () => resolveGa4ListMeta(carouselHeading),
    [carouselHeading]
  );
  const {onSwiper} = useGa4ViewItemList(VIEW_ITEM_LIST.PRODUCT_CAROUSEL, {
    isOnScreen,
    products: instockProducts ?? [],
    ready: Boolean(instockProducts?.length),
  });
  const handleTileClick = useGa4TileSelect(listMeta);

  useEffect(() => {
    if (isWindowsClient()) {
      setWindowsVwNudge(WANT_IT_SOONER_WINDOWS_VW_NUDGE);
    }
  }, []);

  if (!instockProducts?.length) return null;

  return (
    <Box
      ref={carouselRef}
      className="want-it-sooner-carousel"
      {...styles.carouselSection}
      sx={windowsVwNudge ? {'--want-it-sooner-vw-nudge': windowsVwNudge} : undefined}
    >
      <Box {...styles.carouselInner}>
        <Heading {...styles.carouselHeading}>
          {carouselHeading}
        </Heading>

        <Swiper presets="want_it_sooner" onSwiper={onSwiper}>
          {instockProducts.map((product, index) => (
            <InstockProductTile
              key={product.id || product.productId}
              isShowInStockBadge
              product={product}
              showHeroPromotionBadges={false}
              to={getProductUrl(product)}
              variant="wantItSooner"
              onClick={() => handleTileClick(product, index)}
            />
          ))}
        </Swiper>
      </Box>
    </Box>
  );
}

WantItSoonerCarousel.displayName = 'WantItSoonerCarousel';
WantItSoonerCarousel.propTypes = {
  carouselRef: PropTypes.object,
  instockProducts: PropTypes.array,
};

/**
 * Combined hook for the shared carousel ref used by both parts.
 */
export const useWantItSoonerRef = () => useRef(null);
